import { useEffect, useRef, useState } from 'react'
import { Button, DatePicker, Form, Input, Spin } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import {
  useAdvisoryAiReport,
  useAdvisoryAgendaTemplates,
  useAdvisoryInternalMemorySuggestionMutation,
  useUpdateAdvisoryCallMutation,
} from '@/features/advisory-ai/hooks'
import { useToast } from '@/contexts/ToastContext'
import { AdvisoryCallStatus } from '@/features/advisory-ai/types'
import type { AdvisoryAgendaItemSummary } from '@/features/advisory-ai/types'
import {
  agendaTemplateKey,
  withDefaultAgendaTemplate,
  type AgendaTemplateOption,
} from '@/features/advisory-ai/agendaTemplates'
import { isMeetingFinished } from '@/features/advisory-ai/calls/utils'
import { CreateAgendaModal } from './CreateAgendaModal'
import dayjs, { type Dayjs } from 'dayjs'

type AgendaFormValues = {
  title: string
  description: string
}

type AgendaStepProps = {
  callId: string
  clientId: string
  orgId: string
  onContinue: () => void
}

type DraftAgendaItem = {
  advisoryAgendaId: string
  title: string
  description: string
  isNew?: boolean
}

export function AgendaStep({ callId, clientId, orgId, onContinue }: AgendaStepProps) {
  const { addToast } = useToast()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<DraftAgendaItem | null>(null)
  const [meetingTime, setMeetingTime] = useState<Dayjs | null>(null)
  const [internalMemory, setInternalMemory] = useState('')
  const [items, setItems] = useState<DraftAgendaItem[]>([])
  const [form] = Form.useForm<AgendaFormValues>()

  const reportQuery = useAdvisoryAiReport(callId, clientId)
  const templatesQuery = useAdvisoryAgendaTemplates(clientId)
  const updateCallMutation = useUpdateAdvisoryCallMutation(callId, clientId)
  const prefillInternalMemoryMutation = useAdvisoryInternalMemorySuggestionMutation(clientId, orgId)
  const status = String(reportQuery.data?.status ?? '').toUpperCase()
  // Generated insights freeze the agenda and notes. The meeting time stays editable
  // until the stored meeting time passes; the API enforces the same cut-off.
  const isLocked =
    status === AdvisoryCallStatus.COMPLETED_BRIEF || status === AdvisoryCallStatus.COMPLETED_EXPORT
  const isDateLocked = isLocked && isMeetingFinished(reportQuery.data?.meetingDate)

  const callFieldsInitialized = useRef(false)
  useEffect(() => {
    if (!reportQuery.data || callFieldsInitialized.current) return
    callFieldsInitialized.current = true
    const md = reportQuery.data.meetingDate
    if (md) {
      const d = dayjs(md)
      if (d.isValid()) setMeetingTime(d)
    }
    setInternalMemory(reportQuery.data.internalMemory ?? '')
    const existing: DraftAgendaItem[] = ((reportQuery.data.agendaItems ?? []) as AdvisoryAgendaItemSummary[]).map((item) => ({
      advisoryAgendaId: item.advisoryAgendaId,
      title: item.title,
      description: item.description,
    }))
    const reportStatus = String(reportQuery.data.status ?? '').toUpperCase()
    const reportLocked =
      reportStatus === AdvisoryCallStatus.COMPLETED_BRIEF ||
      reportStatus === AdvisoryCallStatus.COMPLETED_EXPORT
    const hasExecutiveSummary = existing.some(
      (item) => item.title.trim().toLowerCase() === 'executive summary'
    )
    if (!hasExecutiveSummary && !reportLocked) {
      existing.unshift({
        advisoryAgendaId: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        title: 'Executive Summary',
        description: '',
        isNew: true,
      })
    }
    setItems(existing)
  }, [reportQuery.data])

  const handleMeetingTimeChange = (value: Dayjs | null) => {
    if (isDateLocked) return
    setMeetingTime(value)
    // Notes are read-only once insights exist, so there is nothing to prefill into.
    if (isLocked || !value || !value.isValid()) return
    prefillInternalMemoryMutation.mutate(value.toISOString(), {
      onSuccess: (data) => {
        const suggestion = data.internalMemory?.trim()
        if (!suggestion) return
        setInternalMemory((current) => {
          if (!current) return suggestion
          const separator = current.endsWith('\n') ? '' : '\n'
          return `${current}${separator}${suggestion}`
        })
      },
    })
  }

  const disabledDate = (current: Dayjs) => {
    if (!current) return false
    return current.endOf('day').isBefore(dayjs(), 'minute')
  }

  const disabledTime = (current: Dayjs | null) => {
    if (!current || !current.isSame(dayjs(), 'day')) return {}
    const now = dayjs()
    return {
      disabledHours: () => Array.from({ length: now.hour() }, (_, i) => i),
      disabledMinutes: (selectedHour: number) =>
        selectedHour === now.hour() ? Array.from({ length: now.minute() }, (_, i) => i) : [],
      disabledSeconds: () => [],
    }
  }

  const handleContinue = async () => {
    if (items.length === 0) return
    const mt = meetingTime
    if (isLocked) {
      const storedDate = reportQuery.data?.meetingDate
      const dateUnchanged =
        !mt || !mt.isValid() || (!!storedDate && dayjs(storedDate).isSame(mt, 'minute'))
      if (isDateLocked || dateUnchanged) {
        onContinue()
        return
      }
      if (!mt.isAfter(dayjs())) {
        addToast('Meeting time must be in the future.', 'error')
        return
      }
      // Date only: sending the agenda would mark the generated exports outdated.
      try {
        await updateCallMutation.mutateAsync({ meetingDate: mt.toISOString() })
        onContinue()
      } catch (error) {
        addToast(error instanceof Error ? error.message : 'Failed to save meeting time', 'error')
      }
      return
    }
    if (!mt || !mt.isValid()) {
      addToast('Meeting date is required.', 'error')
      return
    }
    if (!mt.isAfter(dayjs())) {
      addToast('Meeting time must be in the future.', 'error')
      return
    }
    try {
      await updateCallMutation.mutateAsync({
        meetingDate: mt.toISOString(),
        internalMemory: internalMemory.trim(),
        agendaItems: items.map((item) => ({
          advisoryAgendaId: item.isNew ? undefined : item.advisoryAgendaId,
          title: item.title,
          description: item.description || '',
        })),
      })
      onContinue()
    } catch (error) {
      addToast(error instanceof Error ? error.message : 'Failed to save call details', 'error')
    }
  }

  const handleAdd = () => {
    if (isLocked) return
    setEditingItem(null)
    form.resetFields()
    setModalOpen(true)
  }

  const handleEdit = (item: DraftAgendaItem) => {
    if (isLocked) return
    setEditingItem(item)
    form.setFieldsValue({ title: item.title, description: item.description })
    setModalOpen(true)
  }

  const handleDelete = (item: DraftAgendaItem) => {
    if (isLocked) return
    setItems((current) => current.filter((x) => x.advisoryAgendaId !== item.advisoryAgendaId))
    if (editingItem?.advisoryAgendaId === item.advisoryAgendaId) {
      setEditingItem(null)
    }
    addToast('Agenda item removed', 'success')
  }

  const handleSubmit = async (values: AgendaFormValues) => {
    if (isLocked) return
    const title = values.title.trim()
    const description = values.description || ''
    if (editingItem) {
      setItems((current) =>
        current.map((item) =>
          item.advisoryAgendaId === editingItem.advisoryAgendaId
            ? { ...item, title, description }
            : item
        )
      )
      addToast('Agenda item updated', 'success')
    } else {
      setItems((current) => [
        ...current,
        {
          advisoryAgendaId: `new-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          title,
          description,
          isNew: true,
        },
      ])
    }
    setModalOpen(false)
    form.resetFields()
    setEditingItem(null)
  }

  const handleModalCancel = () => {
    setModalOpen(false)
    form.resetFields()
    setEditingItem(null)
  }

  const handleAddFromTemplate = (template: AgendaTemplateOption) => {
    if (isLocked) return
    const lines = template.items.map((line) => ({
      title: line.title.trim(),
      description: (line.description ?? '').trim(),
    })).filter((line) => line.title.length > 0)
    if (lines.length === 0) return
    const base = Date.now()
    setItems((current) => [
      ...current,
      ...lines.map((line, i) => ({
        advisoryAgendaId: `new-${base}-${i}-${Math.random().toString(16).slice(2)}`,
        title: line.title,
        description: line.description,
        isNew: true,
      })),
    ])
  }

  const isCallSaving = updateCallMutation.isPending
  const isBusy = isCallSaving
  const hasValidMeetingTime = !!meetingTime && meetingTime.isValid()
  const canContinue = items.length > 0 && !isBusy && (isLocked || hasValidMeetingTime)
  const canOpenAddModal = !isBusy && !isLocked
  const canUseTemplates = !isBusy && !isLocked

  if (reportQuery.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spin size="large" />
      </div>
    )
  }

  if (reportQuery.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        {reportQuery.error instanceof Error ? reportQuery.error.message : 'Failed to load call details'}
      </div>
    )
  }

  const templates = templatesQuery.isLoading ? [] : withDefaultAgendaTemplate(templatesQuery.data ?? [])

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Meeting agenda</h2>
        <p className="text-sm text-gray-600">
          Define what you want to cover. The AI will focus its analysis on these topics.
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Meeting time and additional notes are saved when you continue.
        </p>
        {isDateLocked ? (
          <p className="text-sm text-amber-700 mt-2">
            Agenda and meeting time are locked because AI insights have been generated and the meeting time has passed.
          </p>
        ) : isLocked ? (
          <p className="text-sm text-amber-700 mt-2">
            Agenda is locked because AI insights have already been generated. You can still change the meeting time
            until the meeting starts.
          </p>
        ) : null}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="agenda-meeting-time">
          Meeting time <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <DatePicker
          id="agenda-meeting-time"
          showTime
          value={meetingTime}
          onChange={handleMeetingTimeChange}
          disabled={isDateLocked || isBusy}
          disabledDate={disabledDate}
          disabledTime={disabledTime}
          format="DD/MM/YYYY h:mm A"
          className="w-full max-w-xs"
          placeholder="Select date and time"
          aria-required="true"
          aria-describedby="agenda-meeting-time-hint"
        />
        <p id="agenda-meeting-time-hint" className="text-xs text-gray-500 mt-1">
          Only future date and time can be selected.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="agenda-internal-memory">
          Additional notes
          {prefillInternalMemoryMutation.isPending && (
            <span className="ml-2 text-xs text-gray-500 font-normal">
              <Spin size="small" /> Loading suggestion…
            </span>
          )}
        </label>
        <Input.TextArea
          id="agenda-internal-memory"
          value={internalMemory}
          onChange={(event) => setInternalMemory(event.target.value)}
          disabled={isLocked || isBusy}
          rows={5}
          placeholder="Add additional notes for this advisory call..."
          maxLength={4000}
          showCount
          aria-describedby="agenda-internal-memory-hint"
        />
        <p id="agenda-internal-memory-hint" className="text-xs text-gray-500 mt-1">
          Visible only to your team; not shared with the client.
        </p>
      </div>

      {/* data-tour anchors the assistant walkthroughs — see assistant/mockData.ts */}
      <div className="space-y-3" data-tour="agenda-items">
        {items
          .filter((item) => item.title.trim().toLowerCase() !== 'executive summary')
          .map((item) => (
            <div
              key={item.advisoryAgendaId}
              className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4"
            >
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-gray-900">{item.title}</h3>
                <p className="text-sm text-gray-600 mt-0.5">{item.description}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(item)}
                  disabled={isBusy || isLocked}
                  className="text-gray-500 feature-link"
                  aria-label={`Edit ${item.title}`}
                />
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(item)}
                  disabled={isBusy || isLocked}
                  className="text-gray-500 hover:text-red-600"
                  aria-label={`Delete ${item.title}`}
                />
              </div>
            </div>
          ))}
      </div>

      <button
        type="button"
        onClick={handleAdd}
        disabled={!canOpenAddModal}
        className="feature-dashed-action w-full flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 py-4 text-gray-600 disabled:opacity-50 disabled:pointer-events-none"
      >
        <PlusOutlined />
        <span className="font-medium">Add agenda item</span>
      </button>

      {templates.length > 0 && (
        <div className="space-y-2" data-tour="agenda-templates">
          <h3 className="text-sm font-medium text-gray-700">Quick-add from templates</h3>
          <div className="flex flex-wrap gap-2">
            {templates.map((template) => {
              const hasLines = template.items.some((l) => l.title.trim().length > 0)
              return (
                <button
                  key={agendaTemplateKey(template)}
                  type="button"
                  onClick={() => handleAddFromTemplate(template)}
                  disabled={!canUseTemplates || !hasLines}
                  title={!hasLines ? 'This template has no agenda lines' : undefined}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {template.title}
                  {template.kind === 'default' && (
                    <span className="feature-panel-soft feature-label-accent text-xs font-medium rounded-full px-2 py-0.5">
                      Default
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div className="flex justify-end pt-4">
        <Button
          type="primary"
          size="large"
          onClick={handleContinue}
          disabled={!canContinue}
          loading={isCallSaving}
          className="feature-button-solid min-w-[7rem]"
        >
          Continue
        </Button>
      </div>

      <CreateAgendaModal
        open={modalOpen}
        editingItem={editingItem}
        onCancel={handleModalCancel}
        onSubmit={handleSubmit}
        form={form}
        isLoading={isBusy}
      />
    </div>
  )
}
